/**
 * 捐赠项目管理 — GET/POST/PATCH /api/admin/donations/causes
 *
 * Admin CRUD for the donation cause catalog. Causes are soft-archived by
 * flipping `is_active=false` — there is no delete endpoint because donations
 * reference the cause by FK and we never want a dangling donation row.
 *
 * The `code` slug is set once at creation time and is not editable (it acts
 * as a stable URL slug for legacy links / analytics).
 */
import { useState } from 'react';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Tag,
  message,
} from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/DataTable';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface CauseRow {
  id: number;
  code: string;
  title_zh: string;
  title_en: string;
  description_zh: string;
  description_en: string;
  image_url: string | null;
  asset: string;
  goal_amount: string;
  collected_amount: string;
  donor_count: number;
  is_active: boolean;
  sort: number;
  create_time: string;
  update_time: string;
  progress_pct: number;
}

interface CauseCreateForm {
  code: string;
  title_zh: string;
  title_en: string;
  description_zh: string;
  description_en: string;
  image_url?: string;
  asset: 'uo' | 'ep';
  goal_amount: string;
  sort: number;
  is_active: boolean;
}

type CausePatchForm = Omit<CauseCreateForm, 'code'>;

export function DonationCausesPage() {
  const qc = useQueryClient();
  const [isActiveFilter, setIsActiveFilter] = useState<string | undefined>(undefined);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<CauseRow | null>(null);
  const [createForm] = Form.useForm<CauseCreateForm>();
  const [editForm] = Form.useForm<CausePatchForm>();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canCreate = hasNode('donation.cause.create');
  const canEdit = hasNode('donation.cause.update');

  async function onCreate(values: CauseCreateForm) {
    try {
      await api.post('/api/admin/donations/causes', values);
      message.success('捐赠项目已创建');
      setCreateOpen(false);
      createForm.resetFields();
      qc.invalidateQueries({ queryKey: ['admin', 'donation-causes'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '创建失败');
    }
  }

  async function onEdit(values: CausePatchForm) {
    if (!editRow) return;
    try {
      await api.patch(`/api/admin/donations/causes/${editRow.id}`, values);
      message.success('已更新');
      setEditRow(null);
      qc.invalidateQueries({ queryKey: ['admin', 'donation-causes'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '更新失败');
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    {
      title: '封面',
      dataIndex: 'image_url',
      width: 100,
      render: (v: string | null) =>
        v ? (
          <img src={v} alt="" style={{ height: 40, maxWidth: 80, objectFit: 'cover' }} />
        ) : (
          '—'
        ),
    },
    { title: 'Code', dataIndex: 'code', width: 140 },
    { title: '中文标题', dataIndex: 'title_zh', ellipsis: true },
    { title: '英文标题', dataIndex: 'title_en', ellipsis: true, width: 200 },
    {
      title: '资产',
      dataIndex: 'asset',
      width: 80,
      render: (v: string) => <Tag>{v.toUpperCase()}</Tag>,
    },
    { title: '目标', dataIndex: 'goal_amount', width: 130 },
    { title: '已募集', dataIndex: 'collected_amount', width: 130 },
    {
      title: '进度',
      dataIndex: 'progress_pct',
      width: 100,
      render: (v: number) => `${v.toFixed(2)}%`,
    },
    { title: '捐赠人数', dataIndex: 'donor_count', width: 100 },
    { title: '排序', dataIndex: 'sort', width: 70 },
    {
      title: '状态',
      dataIndex: 'is_active',
      width: 90,
      render: (v: boolean) =>
        v ? <Tag color="green">进行中</Tag> : <Tag>已归档</Tag>,
    },
    { title: '创建时间', dataIndex: 'create_time', width: 170 },
    {
      title: '操作',
      key: 'op',
      width: 120,
      fixed: 'right' as const,
      render: (_: unknown, row: CauseRow) => {
        if (!canEdit) return '—';
        return (
          <Button
            type="link"
            size="small"
            onClick={() => {
              setEditRow(row);
              editForm.setFieldsValue({
                title_zh: row.title_zh,
                title_en: row.title_en,
                description_zh: row.description_zh,
                description_en: row.description_en,
                image_url: row.image_url ?? undefined,
                asset: row.asset as 'uo' | 'ep',
                goal_amount: row.goal_amount,
                sort: row.sort,
                is_active: row.is_active,
              });
            }}
          >
            编辑
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>捐赠项目</h2>
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          placeholder="状态"
          style={{ width: 140 }}
          allowClear
          value={isActiveFilter}
          onChange={setIsActiveFilter}
          options={[
            { value: 'true', label: '进行中' },
            { value: 'false', label: '已归档' },
          ]}
        />
        {canCreate && (
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            新建项目
          </Button>
        )}
      </Space>

      <DataTable<CauseRow>
        queryKey={['admin', 'donation-causes', isActiveFilter ?? '']}
        endpoint="/api/admin/donations/causes"
        extraParams={{ is_active: isActiveFilter }}
        columns={columns}
        rowKey="id"
        scroll={{ x: 1800 }}
      />

      <Modal
        open={createOpen}
        title="新建捐赠项目"
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        width={720}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          initialValues={{
            asset: 'uo',
            goal_amount: '0',
            sort: 0,
            is_active: true,
          }}
          onFinish={onCreate}
        >
          <Form.Item
            name="code"
            label="Code (slug)"
            tooltip="创建后不可修改；仅允许小写字母、数字、下划线"
            rules={[
              { required: true, max: 64 },
              { pattern: /^[a-z0-9_]+$/, message: '格式不合法' },
            ]}
          >
            <Input placeholder="例：book_drive_2025" />
          </Form.Item>
          <Form.Item
            name="title_zh"
            label="中文标题"
            rules={[{ required: true, max: 200 }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="title_en"
            label="英文标题"
            rules={[{ required: true, max: 200 }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description_zh"
            label="中文描述"
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="description_en"
            label="英文描述"
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="image_url" label="封面 URL">
            <Input />
          </Form.Item>
          <Form.Item name="asset" label="资产类型" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'uo', label: 'ORB' },
                { value: 'ep', label: 'EP' },
              ]}
            />
          </Form.Item>
          <Form.Item name="goal_amount" label="目标金额">
            <Input />
          </Form.Item>
          <Form.Item name="sort" label="排序">
            <InputNumber />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={editRow !== null}
        title={`编辑项目 · ${editRow?.code ?? ''}`}
        onCancel={() => setEditRow(null)}
        onOk={() => editForm.submit()}
        width={720}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={onEdit}>
          <Form.Item name="title_zh" label="中文标题" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="title_en" label="英文标题" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="description_zh"
            label="中文描述"
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="description_en"
            label="英文描述"
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="image_url" label="封面 URL">
            <Input />
          </Form.Item>
          <Form.Item name="asset" label="资产类型">
            <Select
              options={[
                { value: 'uo', label: 'ORB' },
                { value: 'ep', label: 'EP' },
              ]}
            />
          </Form.Item>
          <Form.Item name="goal_amount" label="目标金额">
            <Input />
          </Form.Item>
          <Form.Item name="sort" label="排序">
            <InputNumber />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
